// Counterpart of webapp/src/store/users.ts

import {create} from 'zustand'
import {IUser} from '../types/user'
import client from '../api/client'

type UsersState = {
    me: IUser | null

    // upstream models "unknown" as null so guarded routes can wait for the answer
    loggedIn: boolean | null
    users: Record<string, IUser>

    fetchMe: () => Promise<IUser | undefined>
    fetchUsers: (teamId: string) => Promise<IUser[]>
    login: (username: string, password: string) => Promise<boolean>
    logout: () => Promise<void>
}

function keyById(users: IUser[]): Record<string, IUser> {
    const out: Record<string, IUser> = {}
    users.forEach((u) => {
        out[u.id] = u
    })
    return out
}

export const useUsers = create<UsersState>((set, get) => ({
    me: null,
    loggedIn: null,
    users: {},

    async fetchMe() {
        const me = await client.getMe()
        if (me && me.id) {
            set({me, loggedIn: true, users: {...get().users, [me.id]: me}})
            return me
        }
        set({me: null, loggedIn: false})
        return undefined
    },

    async fetchUsers(teamId) {
        const users = await client.getUsers(undefined, teamId)
        set((state) => ({users: {...state.users, ...keyById(users)}}))
        return users
    },

    async login(username, password) {
        const ok = await client.login(username, password)
        if (!ok) {
            set({loggedIn: false})
            return false
        }
        await get().fetchMe()
        return true
    },

    async logout() {
        await client.logout()
        set({me: null, loggedIn: false})
    },
}))

export const getMe = (state: UsersState): IUser | null => state.me
export const isLoggedIn = (state: UsersState): boolean | null => state.loggedIn

/** Mirrors Utils.getUserDisplayName() with the default "username" format. */
export function displayName(users: Record<string, IUser>, userId: string | undefined): string {
    const user = userId ? users[userId] : undefined
    if (!user) {
        return '未知用户'
    }
    return user.username
}

export function initials(user: IUser | undefined): string {
    if (!user) {
        return '?'
    }
    const first = user.firstname || user.username.charAt(0)
    const last = user.lastname || ''
    return (first.charAt(0) + last.charAt(0)).toUpperCase()
}
